import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { Button } from "@volca/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { APPROVAL_GATES } from "./approval-types";
import { PeriodApprovalPanel } from "./period-approval-panel";
import type { ApprovalPanelProps, ApprovalQueryState } from "./types";
import { getApprovalCoverage } from "./utils";

interface ApprovalCoverageControlProps extends ApprovalPanelProps {
  approvalStatus?: IPayrollSheetApprovalStatus | null;
  queryState?: ApprovalQueryState;
}

/**
 * Period-wide sign-off, sitting on the same line as "calculated N minutes ago"
 * rather than in its own bordered box — it is standing context about the period,
 * not a call to action competing with the header's buttons. Two counts because
 * the two gates answer different questions ("can PayPacks go out?" and "is payroll
 * done?"), and a single combined number would answer neither.
 */
export const ApprovalCoverageControl = ({
  approvalStatus,
  queryState,
  pendingApproval,
  onApprovalChange,
}: ApprovalCoverageControlProps) => {
  const coverage = getApprovalCoverage(approvalStatus);
  if (queryState?.isLoading)
    return (
      <Stack role="status" direction="row" align="center" gap={1}>
        <Spinner
          layout="centered"
          size="sm"
          tone="secondary"
          className="h-auto w-auto"
        />
        <Text variant="caption" tone="muted">
          Loading approvals
        </Text>
      </Stack>
    );
  if (queryState?.isError)
    return (
      <Stack direction="row" align="center" gap={1}>
        <Text variant="caption" tone="destructive">
          Could not load approvals.
        </Text>
        <Button variant="ghost" size="xs" onClick={queryState.onRetry}>
          Try again
        </Button>
      </Stack>
    );
  if (!approvalStatus || coverage.total === 0) return null;

  // "Approvals 2/9 1/9" is unreadable out loud, so the button carries the spelt
  // out version and the counts stay a visual shorthand.
  const summary = APPROVAL_GATES.map(
    (gate) =>
      `${gate.label}: ${coverage.approvedByType[gate.type]} of ${coverage.total} groups`,
  ).join(", ");

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="xs"
            aria-label={`Approvals. ${summary}`}
            className="group -mx-2 -my-1 gap-2.5 font-normal text-subtle"
          />
        }
      >
        <Text variant="caption">Approvals</Text>
        {APPROVAL_GATES.map(({ type, Icon }) => {
          const approved = coverage.approvedByType[type];
          const isComplete = approved === coverage.total;

          return (
            <Stack key={type} direction="row" align="center" gap={1}>
              <Icon
                aria-hidden="true"
                className={cn("h-3.5 w-3.5", {
                  "text-success": isComplete,
                  "text-subtle": !isComplete,
                })}
              />
              <Text
                weight="semibold"
                variant="caption"
                className={cn({ "text-success": isComplete })}
              >
                {approved}/{coverage.total}
              </Text>
            </Stack>
          );
        })}
        {/* The one thing that says "this opens something". Everything else
                  on this line is static context — "Calculated 5 days ago" sits
                  immediately beside it in the same size and colour — so without a
                  disclosure mark the control reads as a label and goes unclicked. */}
        <CaretDownIcon
          aria-hidden="true"
          className="h-3.5 w-3.5 text-subtle transition-transform duration-200 group-data-popup-open:rotate-180"
        />
      </PopoverTrigger>

      <PopoverContent align="start" className="w-md p-0">
        <PeriodApprovalPanel
          accessKeys={approvalStatus.access_keys}
          pendingApproval={pendingApproval}
          onApprovalChange={onApprovalChange}
        />
      </PopoverContent>
    </Popover>
  );
};
